import { useFilterSearch } from "@/app/hooks/filterValuesSearchHook"
import { useAppDispatch, useAppSelector } from "@/app/hooks/reduxHooks"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { RangeSlider } from "@/components/ui/rangeslider"
import { IFilter, IFilterGroup } from "@/interface/filters/filterGroup"
import { SelectedFilter } from "@/interface/filters/slice"
import { CheckCircle, ChevronDown, ChevronUp, Minus, Plus, Save, Search } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import Funnel from "./../../static/media/icons/filter-icon.svg?react"
import FilterSearchValueResults from "./filterValues"
import { useGetFiltersQuery } from "./slice/apiSlice"
import SaveFilter from "@/components/ui/modals/savefilter"
import Checkbox from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import InfoIcon from "./../../static/media/icons/info-icon.svg?react"
import {
  addSelectedItem,
  removeSelectedItem,
  resetFilters,
  selectExpandedSections,
  selectSearchTerms,
  selectSelectedItems,
  setSearchTerm,
  toggleSection
} from "./slice/filterSlice"

const FilterTag = ({ item, onRemove }: { item: SelectedFilter; onRemove: () => void }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-normal transition-colors ${
      item.type === "include"
        ? "bg-[#335CFF] text-white ring-1 ring-emerald-200/50 dark:bg-[#335CFF] dark:text-white dark:ring-[#335CFF]"
        : "bg-red-200 text-red-950 ring-1 ring-red-200/50 dark:bg-red-950/50 dark:text-red-400 dark:ring-red-800/50"
    }`}
  >
    {item.name}
    <button onClick={onRemove} className="rounded-full p-0.5 transition-colors hover:bg-black/10 dark:hover:bg-white/10" aria-label="Remove filter">
      <svg className="size-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  </span>
)

const SectionHeader = ({ title, expanded, onClick }: { title: string; expanded: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="group flex w-full items-center justify-between p-3.5 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-900/50"
  >
    <span className="text-sm font-medium text-gray-950 dark:text-gray-200">{title}</span>

    <span className="text-gray-400 dark:text-gray-500">{expanded ? <Minus className="size-4" /> : <Plus className="size-4" />}</span>
  </button>
)

export const Filters = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isPeoplePage = location.pathname === "/app/search/contacts"

  const [isSaveOpen, setIsSaveOpen] = useState(false)
  const [yoeRange, setYoeRange] = useState<[number, number]>([0, 5])
  const personalSectionId = "years_of_experience"
  const contactLocationSectionId = "contact_location"

  // Local UI state for Contact Location mock controls
  const [locationSearch, setLocationSearch] = useState("")
  const [selectedCountry, setSelectedCountry] = useState<string>("")
  const [zipCode, setZipCode] = useState("")
  const [radius, setRadius] = useState<string>("")

  // Mock data for the Company group checkbox sections
  const [companyDetails, setCompanyDetails] = useState({
    employeeCount: {
      selected: ["0-50"],
      options: ["0-50", "51-500", "501-1000", "1001-5000", "10000+"]
    },
    companyRevenue: {
      selected: ["0-10M"],
      options: ["0-10M", "11-50M", "51-100M", "101-500M", "501-1B", "1B+"]
    }
  })

  const expandedSections = useAppSelector(selectExpandedSections)
  const searchTerms = useAppSelector(selectSearchTerms)
  const selectedItems = useAppSelector(selectSelectedItems)
  const appliedCount = Object.values(selectedItems).reduce((sum, items) => sum + (items?.length || 0), 0)

  const { currentData: filterGroups = [] } = useGetFiltersQuery()
  const { searchState, handleSearch, clearSearchResults } = useFilterSearch()

  const handleSearchChange = async (filter: IFilter, term: string) => {
    dispatch(setSearchTerm({ sectionId: filter.id, term }))
    if (term) {
      await handleSearch(filter, term)
    } else {
      clearSearchResults(filter.id)
    }
  }
  const toggleCompanyOption = (section: "employeeCount" | "companyRevenue", option: string) => {
    setCompanyDetails((prev) => {
      const current = prev[section].selected
      const nextSelected = current.includes(option) ? current.filter((v) => v !== option) : [...current, option]
      return { ...prev, [section]: { ...prev[section], selected: nextSelected } }
    })
  }

  const renderCheckboxListSection = (args: {
    id: "employee_count" | "company_revenue"
    title: string
    section: "employeeCount" | "companyRevenue"
  }) => {
    const { id, title, section } = args
    const isOpen = !!expandedSections[id]
    const { options, selected } = companyDetails[section]

    return (
      <div
        className={`border-b border-gray-100  dark:border-gray-800/50 ${
          isOpen ? "bg-[#F7F7F7] dark:bg-gray-800/80" : "bg-white dark:bg-transparent"
        }`}
      >
        <SectionHeader title={title} expanded={isOpen} onClick={() => dispatch(toggleSection(id))} />
        {!!isOpen && (
          <div className="space-y-3 p-4">
            {options.map((option) => (
              <label key={option} className="flex cursor-pointer items-center gap-3">
                <Checkbox checked={selected.includes(option)} onChange={() => toggleCompanyOption(section, option)} />
                <span className="text-sm font-normal text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    )
  }

  const currentFiltersGroups: IFilterGroup[] = useMemo(
    () =>
      filterGroups
        .map((group) => ({ ...group, filters: group.filters.filter((filter) => filter.filter_type === (isPeoplePage ? "contact" : "company")) }))
        .filter((group) => group.filters.length > 0),
    [filterGroups, isPeoplePage]
  )

  useEffect(() => {
    dispatch(resetFilters())
  }, [dispatch, location.pathname])

  useEffect(() => {
    const filters = currentFiltersGroups.flatMap((group) => group.filters).filter((filter) => filter.input_type !== "text")
    Object.entries(expandedSections).forEach(([filterId, isExpanded]) => {
      const filter = filters.find((filter) => filter.id === filterId)
      if (isExpanded && filter) handleSearch(filter)
    })
  }, [expandedSections, currentFiltersGroups, handleSearch])

  return (
    <div className="flex flex-col">
      <Card className="mt-4 max-h-[calc(100vh-200px)] min-h-0 min-w-full overflow-auto  rounded-[10px] border bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
        <div className="mb-4 flex items-center justify-between border-b pb-4">
          <span className="flex items-center gap-1 text-sm font-medium text-gray-950">
            <Funnel /> Filter
          </span>
          {appliedCount > 0 && (
            <div className="flex size-4 items-center justify-center rounded-full border border-blue-500 bg-blue-500 p-0.5">
              <span className="text-xs font-normal text-white">{appliedCount}</span>
            </div>
          )}
          <div className="flex flex-row gap-2">
            <Button
              className="border border-gray-200 bg-white p-2 text-sm text-gray-600 hover:bg-transparent"
              onClick={() => dispatch(resetFilters())}
            >
              Clear All
            </Button>
            <Button className="border border-gray-200 bg-white p-2 text-sm text-gray-600 hover:bg-transparent" onClick={() => setIsSaveOpen(true)}>
              <Save /> Save filters
            </Button>
          </div>
        </div>
        {currentFiltersGroups.map((group) => (
          <div key={group.group_name} className="flex flex-col gap-4">
            <div className="mt-4 px-2 py-1">
              <h2 className="text-xs font-semibold text-gray-600 dark:text-gray-400">{group.group_name}</h2>
            </div>
            <Card className="overflow-hidden rounded-lg border bg-transparent shadow-none dark:bg-transparent">
              {group.filters
                .filter((filter) => filter.filter_type === (isPeoplePage ? "contact" : "company"))
                .map((filter) => (
                  <div key={filter.id}>
                    <div
                      key={filter.id}
                      className={`border-b border-gray-100  dark:border-gray-800/50 ${
                        expandedSections[filter.id] ? "bg-[#F7F7F7] dark:bg-gray-800/80" : "bg-white dark:bg-transparent"
                      }`}
                    >
                      <SectionHeader title={filter.name} expanded={expandedSections[filter.id]} onClick={() => dispatch(toggleSection(filter.id))} />
                      {!!expandedSections[filter.id] && (
                        <div className="space-y-3 p-4">
                          {filter.input_type === "text" ? (
                            <form
                              className="relative"
                              onSubmit={(e) => {
                                e.preventDefault()
                                dispatch(
                                  addSelectedItem({
                                    sectionId: filter.id,
                                    item: { id: searchTerms[filter.id] || "", name: searchTerms[filter.id] || "", type: "include" }
                                  })
                                )
                              }}
                            >
                              <input
                                type="text"
                                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-2 pr-9 text-sm text-gray-900 transition-shadow placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-gray-700"
                                placeholder={`Search ${filter.name.toLowerCase()}`}
                                value={searchTerms[filter.id] || ""}
                                onChange={(e) => handleSearchChange(filter, e.target.value)}
                              />
                              <button type="submit">
                                <CheckCircle className="absolute right-3 top-2.5 size-4 cursor-pointer text-green-400 dark:text-green-500" />
                              </button>
                            </form>
                          ) : filter.is_searchable ? (
                            <div className="relative">
                              <Search className="absolute left-3 top-2.5 size-4 text-gray-400 dark:text-gray-500" />
                              <input
                                type="text"
                                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 text-sm text-gray-900 transition-shadow placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-gray-700"
                                placeholder={`Search ${filter.name.toLowerCase()}`}
                                value={searchTerms[filter.id] || ""}
                                onChange={(e) => handleSearchChange(filter, e.target.value)}
                              />
                            </div>
                          ) : null}
                          {selectedItems[filter.id]?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {selectedItems[filter.id].map((item) => (
                                <FilterTag
                                  key={item.id}
                                  item={item}
                                  onRemove={() => dispatch(removeSelectedItem({ sectionId: filter.id, itemId: item.id }))}
                                />
                              ))}
                            </div>
                          )}

                          {filter.input_type !== "text" && (
                            <div className="mt-2">
                              <FilterSearchValueResults
                                results={searchState.results[filter.id]}
                                metadata={searchState.metadata[filter.id]}
                                isLoading={searchState.isLoading[filter.id]}
                                error={searchState.error[filter.id]}
                                selectedItems={selectedItems[filter.id] || []}
                                canExclude={filter.allows_exclusion}
                                onInclude={(item) =>
                                  dispatch(
                                    addSelectedItem({
                                      sectionId: filter.id,
                                      item: { ...item, type: "include" }
                                    })
                                  )
                                }
                                onExclude={(item) =>
                                  dispatch(
                                    addSelectedItem({
                                      sectionId: filter.id,
                                      item: { ...item, type: "exclude" }
                                    })
                                  )
                                }
                                onRemove={(item) => dispatch(removeSelectedItem({ sectionId: filter.id, itemId: item.id }))}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              {/* Custom checkbox sections for Company group */}
              {group.group_name.toLowerCase() === "company" && (
                <>
                  {renderCheckboxListSection({ id: "employee_count", title: "Employee Count", section: "employeeCount" })}
                  {renderCheckboxListSection({ id: "company_revenue", title: "Company Revenue", section: "companyRevenue" })}
                </>
              )}
              {group.group_name.toLowerCase() === "personal" && (
                <div
                  className={`border-b border-gray-100  dark:border-gray-800/50 ${
                    expandedSections[personalSectionId] ? "bg-[#F7F7F7] dark:bg-gray-800/80" : "bg-white dark:bg-transparent"
                  }`}
                >
                  {/* Contact Location */}
                  <div
                    className={`border-b border-gray-100  dark:border-gray-800/50 ${
                      expandedSections[contactLocationSectionId] ? "bg-[#F7F7F7] dark:bg-gray-800/80" : "bg-white dark:bg-transparent"
                    }`}
                  >
                    <SectionHeader
                      title="Contact Location"
                      expanded={!!expandedSections[contactLocationSectionId]}
                      onClick={() => dispatch(toggleSection(contactLocationSectionId))}
                    />
                    {!!expandedSections[contactLocationSectionId] && (
                      <div className="space-y-4 p-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-950">
                            <span className="text-xs font-medium text-gray-600  dark:text-gray-400">Contact location</span>
                            <InfoIcon className="size-3.5 text-gray-400" />
                          </div>
                          <div className="relative">
                            <Search className="absolute left-3 top-2.5 size-4  text-gray-400 dark:text-gray-500" />
                            <Input
                              placeholder="Search Location"
                              className="bg-white pl-9 text-xs font-medium text-gray-400 dark:bg-gray-800"
                              value={locationSearch}
                              onChange={(e) => setLocationSearch(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="space-y-3">
                            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                              <SelectTrigger className="h-11 bg-white p-[10px] text-sm font-medium text-gray-400 dark:bg-gray-800 dark:text-gray-400">
                                <SelectValue placeholder="Select Country" />
                              </SelectTrigger>
                              <SelectContent className="text-sm font-medium text-gray-950">
                                <SelectItem value="us">United States</SelectItem>
                                <SelectItem value="ca">Canada</SelectItem>
                                <SelectItem value="gb">United Kingdom</SelectItem>
                              </SelectContent>
                            </Select>

                            <div className="flex items-center gap-1 text-sm font-medium text-gray-950">
                              <span className="text-xs font-medium text-gray-600  dark:text-gray-400">ZIP Code Radius</span>
                              <InfoIcon className="size-3.5 text-gray-400" />
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <Input
                                placeholder="Enter ZIP"
                                inputMode="numeric"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                                className="bg-white text-xs font-medium text-gray-400 dark:bg-gray-800"
                              />
                              <Select value={radius} onValueChange={setRadius}>
                                <SelectTrigger className=" bg-white p-[10px] text-xs font-medium text-gray-400 dark:bg-gray-800">
                                  <SelectValue placeholder="Select Radius" />
                                </SelectTrigger>
                                <SelectContent className="text-xs font-normal text-gray-950">
                                  <SelectItem value="10">10 miles</SelectItem>
                                  <SelectItem value="25">25 miles</SelectItem>
                                  <SelectItem value="50">50 miles</SelectItem>
                                  <SelectItem value="100">100 miles</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <SectionHeader
                    title="Years of Experience"
                    expanded={!!expandedSections[personalSectionId]}
                    onClick={() => dispatch(toggleSection(personalSectionId))}
                  />
                  {!!expandedSections[personalSectionId] && (
                    <div className="p-4">
                      <RangeSlider min={0} max={30} step={1} unit="YOE" hideHeader value={yoeRange} onChange={setYoeRange} />
                    </div>
                  )}
                </div>
              )}
              {group.group_name.toLowerCase() === "demographics" && <div></div>}
            </Card>
          </div>
        ))}
      </Card>
      <SaveFilter open={isSaveOpen} onOpenChange={setIsSaveOpen} />
    </div>
  )
}

export default Filters
